/** Global engine constants. */

export const SEED = 1337;

/** Horizontal chunk footprint. Chunks are full-height columns. */
export const CHUNK_SIZE = 16;
/**
 * World height in blocks (Y = 0 .. WORLD_HEIGHT-1). Vanilla's -64..319 is
 * mapped onto this range at half vertical scale around sea level (see
 * worldgen/terrain.ts `vy`). The mesher packs y into 8 bits, so the cap is 254.
 */
export const WORLD_HEIGHT = 192;
/** Sea level for oceans / rivers (vanilla Y 63). */
export const SEA_LEVEL = 63;

/** Chunks generated/meshed around the player (Manhattan-ish radius). */
export const LOAD_RADIUS = 8;
/** Radius at which chunks get unloaded (hysteresis > LOAD_RADIUS). */
export const UNLOAD_RADIUS = LOAD_RADIUS + 2;

/** Max chunk jobs handed to workers per frame (gen+mesh), keeps frame budget. */
export const MAX_MESH_UPLOADS_PER_FRAME = 4;

/**
 * Player physics — vanilla Minecraft (Java Edition) values. Units are blocks
 * and game ticks (20 per second), so velocities are blocks/tick exactly as in
 * the original; the renderer interpolates between ticks.
 */
export const TICK_RATE = 20;
export const TICK_DT = 1 / TICK_RATE;

export const PLAYER_WIDTH = 0.6;
export const PLAYER_HEIGHT = 1.8;
export const PLAYER_EYE = 1.62;
export const SNEAK_HEIGHT = 1.5;
export const SNEAK_EYE = 1.27;
export const SWIM_HEIGHT = 0.6;
export const SWIM_EYE = 0.4;

export const GRAVITY = 0.08;
/** Vertical velocity multiplier applied after gravity each tick. */
export const VERTICAL_DRAG = 0.98;
/** Horizontal velocity multiplier per tick (times block slipperiness on ground). */
export const HORIZONTAL_DRAG = 0.91;
export const DEFAULT_SLIPPERINESS = 0.6;
export const JUMP_VELOCITY = 0.42;
/** Horizontal kick along the facing direction when jumping while sprinting. */
export const SPRINT_JUMP_BOOST = 0.2;
/** Ticks before an held jump key can jump again. */
export const JUMP_DELAY_TICKS = 10;

/** Movement-speed attribute; ground acceleration is derived from it. */
export const WALK_SPEED = 0.1;
export const SPRINT_SPEED_MULT = 1.3;
export const AIR_ACCEL = 0.02;
export const SPRINT_AIR_ACCEL = 0.025999999;
/** Key input is scaled by this every tick before it is applied. */
export const INPUT_SCALE = 0.98;
export const SNEAK_INPUT_SCALE = 0.3;
/** Sneaking refuses to walk off ledges taller than this. */
export const STEP_HEIGHT = 0.6;

/** Creative flight: horizontal acceleration (doubled when sprinting). */
export const FLY_SPEED = 0.05;
export const FLY_VERTICAL_DRAG = 0.6;

export const LIQUID_ACCEL = 0.02;
export const WATER_DRAG = 0.8;
export const WATER_SPRINT_DRAG = 0.9;
export const LAVA_DRAG = 0.5;
/** Vertical push per tick from holding jump (up) or sneak (down) in a liquid. */
export const LIQUID_SWIM_IMPULSE = 0.04;
/** Upward velocity when swimming into a ledge that can be climbed out onto. */
export const LIQUID_EXIT_VELOCITY = 0.3;
/** Height of a liquid's surface within its block when open to the air. */
export const LIQUID_SURFACE_HEIGHT = 8 / 9;

/** Window (ticks) for double-tapping forward to sprint / jump to toggle flight. */
export const DOUBLE_TAP_TICKS = 7;

export const REACH = 5.5;
export const MOUSE_SENSITIVITY = 0.0023;

/** Full day length in seconds (real time). */
export const DAY_LENGTH = 600;

export const FOV = 75;
export const NEAR_PLANE = 0.08;
export const FAR_PLANE = 1200;

export const WORKER_POOL_SIZE = Math.min(
  Math.max(2, (typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : 8) - 1),
  6
);
